import { gql, NetworkStatus, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  MyRankingQuery,
  MyRankingQueryVariables,
  RankingBaseConnectionFragmentDoc,
  RankingPeriodBaseConnectionFragmentDoc,
  RankingPeriodType,
  RankingUserWithoutIdConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { dayjs } from '@/lib/dayjs'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, generatePath, routerPages } =
    useRouter<{ tab?: 'total' | 'monthly' }>()
  const { data, networkStatus } = useQuery<
    MyRankingQuery,
    MyRankingQueryVariables
  >(QUERY, {
    variables: {
      periodType:
        router.query.tab === 'total'
          ? RankingPeriodType.Yearly
          : RankingPeriodType.Monthly,
      pageInput: { limit: 10 },
    },
    returnPartialData: true,
    notifyOnNetworkStatusChange: true,
  })

  const period = useMemo(
    () => `${dayjs().startOf('month').format('ll')}〜${dayjs().format('ll')}`,
    []
  )

  return {
    state: {
      loading:
        networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.setVariables,
      refetching: networkStatus === NetworkStatus.fetchMore,
      rankings: data?.organizationArtistRankings ?? null,
      hasNextPage: false,
      periodType:
        router.query.tab === 'total'
          ? RankingPeriodType.Yearly
          : RankingPeriodType.Monthly,
      period,
    },
    actions: {
      async onFetchMore() {},
      async onChangePeriod(periodType: RankingPeriodType) {
        router.replace(
          generatePath(routerPages.MyRanking, {
            tab: periodType === RankingPeriodType.Yearly ? 'total' : 'monthly',
          })
        )
      },
    },
  }
})

const QUERY = gql`
  query MyRanking($periodType: RankingPeriodType!, $pageInput: PageInput) {
    organizationArtistRankings(pageInput: $pageInput) {
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          ... on Ranking {
            ...RankingBaseConnection
            latestRankingPeriod(periodType: $periodType) {
              ...RankingPeriodBaseConnection
              myRanking {
                ...RankingUserWithoutIdConnection
              }
            }
          }
        }
      }
    }
  }
  ${RankingBaseConnectionFragmentDoc}
  ${RankingPeriodBaseConnectionFragmentDoc}
  ${RankingUserWithoutIdConnectionFragmentDoc}
`
